import { useEffect, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { EmptyState, LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatNumber } from '@/i18n/format';
import type { CatalogRow } from '@/api/types';

const LIMIT = 25;
const DASH = '—';

/**
 * The seller's permanent product catalogue: every SKU / variant of their company, found by
 * article, name or barcode, whether or not it has stock (stock lives on the Inventory page).
 * `highlightBatch` marks the goods a just-confirmed import created; `refreshKey` re-reads the list.
 * On a phone the table turns into one card per product (no sideways scrolling).
 */
export function CatalogList({ highlightBatch, refreshKey }: { highlightBatch: string | null; refreshKey: number }): JSX.Element {
  const { t, locale } = useLanguage();
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);

  // Typing searches after a short pause, from the first page.
  useEffect(() => {
    const id = setTimeout(() => {
      setQ(text.trim());
      setOffset(0);
    }, 250);
    return () => clearTimeout(id);
  }, [text]);

  const result = useApi(
    () => endpoints.catalog({ ...(q ? { q } : {}), ...(highlightBatch ? { batch: highlightBatch } : {}), limit: LIMIT, offset }),
    [q, offset, highlightBatch, refreshKey],
  );

  const kg = (v: number | null): string => (v === null ? DASH : formatNumber(v, locale, { maximumFractionDigits: 3 }));
  const cm = (v: number | null): string => (v === null ? DASH : formatNumber(v, locale, { maximumFractionDigits: 1 }));
  const dims = (r: CatalogRow): string => (r.lengthCm === null && r.widthCm === null && r.heightCm === null ? DASH : `${cm(r.lengthCm)}×${cm(r.widthCm)}×${cm(r.heightCm)}`);

  return (
    <section aria-label={t('catalog.listLabel')}>
      <div className="filters">
        <input
          type="search"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('catalog.search')}
          aria-label={t('catalog.search')}
          maxLength={100}
          style={{ flex: '1 1 260px' }}
        />
      </div>
      {result.status === 'loading' && <LoadingState />}
      {result.status === 'error' && <ErrorBanner error={result.error} onRetry={result.reload} />}
      {result.status === 'success' && result.data.rows.length === 0 && (
        q ? (
          <EmptyState title={t('catalog.emptySearch.title')} hint={t('catalog.emptySearch.hint')} />
        ) : (
          <EmptyState title={t('catalog.empty.title')} hint={t('catalog.empty.hint')} />
        )
      )}
      {result.status === 'success' && result.data.rows.length > 0 && (
        <>
          <table className="stack-table" aria-label={t('catalog.listLabel')}>
            <thead>
              <tr>
                <th>{t('catalog.col.article')}</th>
                <th>{t('catalog.col.name')}</th>
                <th>{t('catalog.col.size')}</th>
                <th>{t('catalog.col.color')}</th>
                <th>{t('catalog.col.barcode')}</th>
                <th>{t('catalog.col.weight')}</th>
                <th>{t('catalog.col.dims')}</th>
              </tr>
            </thead>
            <tbody>
              {result.data.rows.map((r) => (
                <tr key={r.id} className={r.imported ? 'row-new' : undefined}>
                  <td data-label={t('catalog.col.article')}>
                    <div>
                      <strong>{r.article}</strong>
                      {r.code !== r.article && <div className="sub">{r.code}</div>}
                    </div>
                  </td>
                  <td data-label={t('catalog.col.name')}>
                    <div>
                      {r.name}
                      {r.imported && <span className="badge ok" style={{ marginLeft: 6 }}>{t('catalog.imported')}</span>}
                    </div>
                  </td>
                  <td data-label={t('catalog.col.size')}>{r.size || DASH}</td>
                  <td data-label={t('catalog.col.color')}>{r.color || DASH}</td>
                  <td data-label={t('catalog.col.barcode')}>
                    {r.barcodes.length === 0 ? DASH : <div>{r.barcodes.map((b) => <div key={b}>{b}</div>)}</div>}
                  </td>
                  <td data-label={t('catalog.col.weight')}>{kg(r.weightKg)}</td>
                  <td data-label={t('catalog.col.dims')}>{dims(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination">
            <span>
              {formatNumber(offset + 1, locale)}–{formatNumber(Math.min(offset + result.data.rows.length, result.data.total), locale)} {t('common.of')}{' '}
              {formatNumber(result.data.total, locale)}
            </span>
            <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>{t('common.previous')}</button>
            <button type="button" disabled={offset + result.data.rows.length >= result.data.total} onClick={() => setOffset(offset + LIMIT)}>{t('common.next')}</button>
          </div>
        </>
      )}
    </section>
  );
}
